'use client';

import type { ReactNode } from 'react';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import type {
  CompanyProfileComponent,
  TransformedCompanyProfileData,
} from '@/types/company-profile';
import type { CommonSpacerZone } from '@/types/homepage';
import CompanyProfileShell from './CompanyProfileShell';
import CompanyProfileHero from './CompanyProfileHero';
import CompanyProfileAccreditations from './CompanyProfileAccreditations';
import CompanyProfileMarvels from './CompanyProfileMarvels';
import CompanyProfileTestimonials from './CompanyProfileTestimonials';
import CompanyProfileTradeEnquiry from './CompanyProfileTradeEnquiry';
import type { ContactForm } from '@/types/contact-forms';
import { hasRichTextContent } from '@/components/ui/CmsRichTextHeading';

interface CompanyProfileProps {
  locale: string;
  data: TransformedCompanyProfileData;
  emailContactForm?: ContactForm | null;
}

function isSpacer(component: CompanyProfileComponent): component is CommonSpacerZone {
  return (
    component.__component === 'common.spacer-top' ||
    component.__component === 'common.spacer-full' ||
    component.__component === 'common.spacer-bottom'
  );
}

function renderCompanyProfileComponent(component: CompanyProfileComponent, locale: string) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={`${component.__component}-${component.id}`} zone={component} />;

    case 'infrastructure.logo-grid-section':
      return <CompanyProfileAccreditations key={component.id} zone={component} />;

    case 'infrastructure.infra-projects':
      return <CompanyProfileMarvels key={component.id} locale={locale} zone={component} />;

    case 'common.testimonial':
      return <CompanyProfileTestimonials key={component.id} zone={component} />;

    case 'infrastructure.trade-enquiries':
      return <CompanyProfileTradeEnquiry key={component.id} locale={locale} zone={component} />;

    default:
      return null;
  }
}

/** Render the dark "marvels" wrap that absorbs its adjacent spacers. */
function renderMarvelsWrap(
  component: Extract<CompanyProfileComponent, { __component: 'infrastructure.infra-projects' }>,
  topSpacer: CommonSpacerZone | null,
  bottomSpacer: CommonSpacerZone | null,
  locale: string,
): ReactNode {
  return (
    <div key={`marvels-wrap-${component.id}`} className="bg-black text-white">
      {topSpacer ? (
        <CommonSpacer key={`${topSpacer.__component}-${topSpacer.id}`} zone={topSpacer} />
      ) : null}
      <CompanyProfileMarvels locale={locale} zone={component} />
      {bottomSpacer ? (
        <CommonSpacer key={`${bottomSpacer.__component}-${bottomSpacer.id}`} zone={bottomSpacer} />
      ) : null}
    </div>
  );
}

/** Handle the "marvels" section at index `i`: pop the previous spacer node if it
 *  became the wrap's top spacer, push the wrap, and report how far to advance. */
function appendMarvelsWrap(
  component: Extract<CompanyProfileComponent, { __component: 'infrastructure.infra-projects' }>,
  components: CompanyProfileComponent[],
  i: number,
  nodes: ReactNode[],
  locale: string,
): number {
  const prev = i > 0 ? components[i - 1] : null;
  const next = i + 1 < components.length ? components[i + 1] : null;
  const topSpacer = prev && isSpacer(prev) ? prev : null;
  const bottomSpacer = next && isSpacer(next) ? next : null;

  // If previous was already rendered as a normal spacer, replace that node with the wrap.
  if (topSpacer && nodes.length > 0) {
    nodes.pop();
  }

  nodes.push(renderMarvelsWrap(component, topSpacer, bottomSpacer, locale));

  // Skip the trailing spacer we just consumed, plus the current component.
  return bottomSpacer ? 2 : 1;
}

/** Walk the polymorphic component list, wrapping the "marvels" section together
 *  with its adjacent spacers in a dark band. */
function buildCompanyProfileNodes(
  components: CompanyProfileComponent[],
  locale: string,
): ReactNode[] {
  const nodes: ReactNode[] = [];

  let i = 0;
  while (i < components.length) {
    const component = components[i];

    if (component.__component === 'infrastructure.infra-projects') {
      i += appendMarvelsWrap(component, components, i, nodes, locale);
      continue;
    }

    nodes.push(renderCompanyProfileComponent(component, locale));
    i += 1;
  }

  return nodes;
}

export default function CompanyProfile({ locale, data, emailContactForm }: CompanyProfileProps) {
  const hasBanner =
    Boolean(data.bannerHeading?.trim()) ||
    hasRichTextContent(data.titleRichText) ||
    Boolean(data.bannerSubheading?.trim()) ||
    Boolean(data.bannerImage);

  const nodes = buildCompanyProfileNodes(data.components, locale);

  return (
    <CompanyProfileShell emailContactForm={emailContactForm}>
      <main className="min-h-screen overflow-x-hidden bg-white text-black">
        {hasBanner ? (
          <CompanyProfileHero
            locale={locale}
            bannerHeading={data.bannerHeading}
            titleRichText={data.titleRichText}
            bannerSubheading={data.bannerSubheading}
            bannerImage={data.bannerImage}
            bannerButton={data.bannerButton}
          />
        ) : null}

        {nodes}
      </main>
    </CompanyProfileShell>
  );
}
